import { useState, useEffect } from 'react';
// utils
import { isLocalStorageEvent, safeGetItem, safeSetItem } from '../utils/safeStorage';

// ----------------------------------------------------------------------

function parseStoredValue<ValueType>(storedValue: string | null, defaultValue: ValueType): ValueType {
  if (storedValue === null) {
    return defaultValue;
  }
  try {
    return JSON.parse(storedValue);
  } catch {
    return defaultValue;
  }
}

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType) {
  const [value, setValue] = useState(() => parseStoredValue(safeGetItem('local', key), defaultValue));

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (isLocalStorageEvent(e) && e.key === key) {
        setValue(parseStoredValue(e.newValue, defaultValue));
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: ValueType) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;

      if (result !== undefined) {
        safeSetItem('local', key, JSON.stringify(result));
      }

      return result;
    });
  };

  return [value, setValueInLocalStorage] as const;
}
